<template>
    <div class="payment-methods-container">
        <div id="preloader" v-if="load" class="preloader"></div>
        <div class="payment-methods-loaded">
            <span class="payment-methods-title">My Saved Cards</span>

            <div
                v-for="(paymentMethod, indx) in paymentMethods"
                :key="paymentMethod.id"
                class="payment-method"
            >
                <div class="d-flex align-items-center card-body">
                    <div class="card-icon">
                        <img
                            :src="
                                paymentMethod.brand
                                    ? ccBrandIcons[paymentMethod.brand]
                                    : ccBrandIcons.others
                            "
                        />
                    </div>
                    <div class="card-info">
                        <div class="card-dates-info">
                            <div>
                                <span>ending in {{ paymentMethod.last_four }}</span>
                            </div>
                            <div>
                                <span v-if="handleStatus(paymentMethod) === 'expired'">
                                    expired {{ paymentMethod.exp_month }}/{{
                                        paymentMethod.exp_year
                                    }}
                                </span>
                                <span v-else>
                                    expires {{ paymentMethod.exp_month }}/{{
                                        paymentMethod.exp_year
                                    }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="card-status">
                        <button
                            v-if="!handleStatus(paymentMethod)"
                            @click.prevent="handleDefaultPress(paymentMethod.id)"
                            class="set-default-btn"
                        >
                            Make Default
                        </button>
                        <div v-if="handleStatus(paymentMethod) === 'invalid'" class="invalid">
                            <span class="invalid-text">Invalid</span>
                        </div>
                        <div v-if="handleStatus(paymentMethod) === 'expired'" class="expired">
                            <span class="expired-text">Expired</span>
                        </div>
                        <div v-if="handleStatus(paymentMethod) === 'default'" class="default">
                            <span class="default-text"> Default Card </span>
                        </div>
                    </div>
                    <div class="d-flex px-2" style="order: 3; gap: 10px">
                        <div class="card-edit">
                            <img
                                @click="handleUpdatePress(indx)"
                                src="/images/icons/payment-details/Pencil.svg"
                                role="button"
                                tabindex="0"
                            />
                        </div>
                        <div class="card-delete px-2">
                            <img
                                @click="handleDeletePress(paymentMethod.id)"
                                src="/images/icons/payment-details/Card.svg"
                                role="button"
                                tabindex="0"
                            />
                        </div>
                    </div>

                    <div class="break"></div>
                </div>
                <div
                    class="update-payment-method-container"
                    v-if="formStates.showUpdateForm[indx]"
                >
                    <card-form
                        op="update"
                        :index="indx"
                        :csrf="csrf"
                        :paymentMethod="paymentMethod"
                        @clicked-form-submit="handleChildSubmit"
                        @clicked-form-cancel="clearChildForms"
                    >
                    </card-form>
                </div>
            </div>
            <button
                v-if="!formStates.showCreateForm"
                @click="toggleForm('create', true)"
                class="new-payment-method-btn"
                id="add-new-btn"
            >
                <span class="new-payment-method-text">Add New Card</span>
            </button>
            <card-form
                op="create"
                v-if="formStates.showCreateForm"
                :csrf="csrf"
                @clicked-form-submit="handleChildSubmit"
                @clicked-form-cancel="clearChildForms"
                :paymentMethods="paymentMethods"
            >
            </card-form>
        </div>
        <confirmation-modal
            v-if="showModal"
            @clicked-yes="handleChildModalYes"
            @clicked-no="handleChildModalNo"
        ></confirmation-modal>
    </div>
</template>

<script>
    import axios from 'axios'
    import CardForm from './CardForm.vue'
    import ConfirmationModal from './ConfirmationModal.vue'
    import useNotification from '@/composables/useNotification.js'

    export default {
        name: 'PaymentDetails',
        components: { CardForm, ConfirmationModal },
        setup() {
            const { notification } = useNotification()

            return {
                notification
            }
        },
        data() {
            return {
                load: false,
                csrf: document.head.querySelector('meta[name="csrf-token"]')['content'],
                paymentMethods: [],
                ccBrandIcons: {
                    'American Express': '/images/icons/payment-details/cc-amex.svg',
                    Visa: '/images/icons/payment-details/cc-visa.svg',
                    MasterCard: '/images/icons/payment-details/cc-mastercard.svg',
                    Discover: '/images/icons/payment-details/cc-discover.svg',
                    UnionPay: '/images/icons/payment-details/UnionPay.svg',
                    DinaCard: '/images/icons/payment-details/DinaCard.svg',
                    'Diners Club': '/images/icons/payment-details/DinersClub.svg',
                    BCCard: '/images/icons/payment-details/BCCard.svg',
                    JCB: '/images/icons/payment-details/JSB.svg',
                    others: '/images/icons/payment-details/cc-regular.svg'
                },
                formStates: {
                    showCreateForm: false,
                    showUpdateForm: {}
                },

                showModal: false,
                delPaymentId: ''
            }
        },

        methods: {
            async loadPaymentMethods(checkFav = false) {
                this.paymentMethods.splice(0)

                try {
                    this.load = true
                    let resp = await axios.get('/api/profile/payment-details/all')
                    let currPMs = resp.data
                    for (let i = 0; i < currPMs.length; i++) {
                        this.paymentMethods.push(currPMs[i])
                    }
                    this.initUpdateFormStates()

                    if (checkFav) await this.handleFavouriteMethod()

                    return resp
                } catch (e) {
                    this.notification('error', e)
                } finally {
                    this.load = false
                }
            },

            initUpdateFormStates() {
                for (let i = 0; i < this.paymentMethods.length; i++) {
                    this.$set(this.formStates.showUpdateForm, i, false)
                }
            },

            async handleFavouriteMethod() {
                let fav = null

                for (let i = 0; i < this.paymentMethods.length; i++) {
                    if (this.paymentMethods[i].favourite) {
                        fav = this.paymentMethods[i]
                        break
                    }
                }

                if (
                    fav &&
                    fav.verified &&
                    !this.hasPaymentExpired(fav.exp_month, fav.exp_year)
                ) {
                    return
                } else {
                    await this.findNextFavPayment()
                }
            },

            async findNextFavPayment() {
                for (let i = 0; i < this.paymentMethods.length; i++) {
                    let pm = this.paymentMethods[i]

                    if (pm.verified && !this.hasPaymentExpired(pm.exp_month, pm.exp_year)) {
                        await this.handleDefaultPress(pm.id)
                        break
                    }
                }
            },

            hasPaymentExpired(expMonth, expYear) {
                if (expYear.length === 2) expYear = '20' + expYear
                return new Date() > new Date(expYear, expMonth)
            },

            toggleForm(fm, state, opt) {
                if (fm === 'create') {
                    this.formStates.showCreateForm = state
                } else {
                    this.formStates.showUpdateForm[opt.key] = state
                }
            },

            handleStatus(paymentMethod) {
                /** @TODO: uncomment, once backend is finished to update payment verification checking */

                // if (!paymentMethod.verified) {
                //     return 'invalid'
                // } else if (

                if (this.hasPaymentExpired(paymentMethod.exp_month, paymentMethod.exp_year)) {
                    return 'expired'
                } else if (paymentMethod.favourite) {
                    return 'default'
                } else {
                    return ''
                }
            },

            async handleChildSubmit(fm) {
                await this.loadPaymentMethods()
                this.clearChildForms(fm)
            },

            clearChildForms(fm) {
                if (fm === 'create') {
                    this.toggleForm('create', false)
                } else {
                    // fm format is: update:[key]
                    let key = fm.split(':')[1]

                    this.toggleForm('update', false, { key })
                }
            },

            async handleChildModalYes() {
                try {
                    this.load = true
                    let resp = await axios.delete(
                        `/profile/payment-details/${this.delPaymentId}`
                    )
                    await this.loadPaymentMethods()
                    this.showModal = false

                    return resp
                } catch (e) {
                    this.notification('error', e)
                } finally {
                    this.load = false
                }
            },

            handleChildModalNo() {
                this.showModal = false
            },

            handleUpdatePress(indx) {
                this.toggleForm('update', true, { key: indx })
            },

            handleDeletePress(paymentMethodId) {
                this.showModal = true
                this.delPaymentId = paymentMethodId.toString()
            },

            async handleDefaultPress(id) {
                try {
                    this.load = true
                    let resp = await axios.get(`/profile/checkout-payment-method/${id}`)
                    await this.loadPaymentMethods()

                    return resp
                } catch (e) {
                    this.notification('error', e)
                } finally {
                    this.load = false
                }
            }
        },

        async mounted() {
            await this.loadPaymentMethods(true)
        },

        updated() {
            if (this.showModal) {
                document.documentElement.style.overflow = 'hidden'
            } else {
                document.documentElement.style.overflow = 'auto'
            }
        }
    }
</script>

<style lang="scss">
    $small: 320px;
    $medium: 768px;

    .payment-methods-container {
        font-family: Instrument Sans;
        margin-left: 50px;

        .payment-methods-loaded {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        }

        .payment-methods-title {
            color: #fff;
            font-feature-settings: 'clig' off, 'liga' off;
            font-size: 30px;
            font-style: normal;
            font-weight: 700;
            line-height: 38px;
        }

        .payment-method {
            display: flex;
            flex-direction: column;
            flex-wrap: no-wrap;
            justify-content: space-between;
            align-items: flex-start;

            .card-body {
                min-width: 550px;
                border-bottom: 1px solid #8d8d96;

                .card-icon {
                    flex-grow: 1;
                    max-width: 75px;
                    width: 100%;

                    img {
                        height: 40px;
                        width: auto;
                    }
                }
                .card-dates-info {
                    display: flex;
                    flex-direction: column;
                    flex: 1;
                }
                .card-info {
                    flex-grow: 1;
                    max-width: 135px;
                    width: 100%;
                    color: #fff;
                }

                .card-status {
                    flex-grow: 1;
                    max-width: 160px;
                    width: 100%;
                    color: #fff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    .invalid {
                        margin-bottom: 0px;
                        border-radius: 20px;
                        border: 1px solid #1b0d29;
                        background: #ffb6b6;
                        display: flex;
                        padding: 4px 12px;
                        justify-content: center;
                        align-items: center;
                        gap: 8px;
                    }
                    .default {
                        display: flex;
                        padding: 4px 12px;
                        justify-content: center;
                        align-items: center;
                        gap: 8px;
                        border-radius: 20px;
                        border: 1px solid #fff;
                        border-radius: 20px;
                        border: 1px solid #fff;
                        .default-text {
                            color: #fff;
                            text-align: center;
                            font-feature-settings: 'clig' off, 'liga' off;
                            font-family: Inter;
                            font-size: 14px;
                            font-style: normal;
                            font-weight: 500;
                            line-height: 20px;
                        }
                    }
                    .expired {
                        display: flex;
                        padding: 4px 12px;
                        justify-content: center;
                        align-items: center;
                        gap: 8px;
                        border-radius: 20px;
                        border: 1px solid #1b0d29;
                        background: #ffb6b6;
                        .expired-text {
                            color: #1b0d29;
                            text-align: center;
                            font-feature-settings: 'clig' off, 'liga' off;
                            font-family: Inter;
                            font-size: 14px;
                            font-style: normal;
                            font-weight: 500;
                            line-height: 20px;
                        }
                    }

                    .set-default-btn {
                        display: flex;
                        padding: 4px 12px;
                        justify-content: center;
                        align-items: center;
                        gap: 8px;
                        border-radius: 20px;
                        border: 1px solid #1b0d29;
                        background: #d7eb5a;
                        .default-text {
                            color: #1b0d29;
                            text-align: center;
                            font-feature-settings: 'clig' off, 'liga' off;
                            font-family: Inter;
                            font-size: 14px;
                            font-style: normal;
                            font-weight: 500;
                            line-height: 20px;
                        }
                    }
                }

                .card-edit {
                    flex-grow: 1;
                    max-width: 60px;
                }

                .card-delete {
                    order: 5;
                    flex-grow: 1;
                    text-align: right;
                }

                .card-edit,
                .card-delete {
                    img {
                        height: 22px;
                        width: auto;
                    }
                }

                .break {
                    flex-basis: 100%;
                    height: 0;
                }

                .update-payment-method-container {
                    order: 6;
                    flex-grow: 1;
                }
            }
        }

        .new-payment-method-btn {
            border-radius: 100px;
            background: #d7eb5a;
            color: rgb(0, 0, 0);
            margin-top: 35px;
            display: inline-flex;
            padding: 16px 48px;
            justify-content: center;
            align-items: center;
            gap: 16px;
            border: none;

            .new-payment-method-text {
                color: #161616;
                font-feature-settings: 'clig' off, 'liga' off;
                font-family: Instrument Sans;
                font-size: 16px;
                font-style: normal;
                font-weight: 600;
                line-height: 24px;
            }
        }
    }
    @media screen and (min-width: $small) and (max-width: $medium) {
        .payment-methods-container {
            margin-left: 0px;
            width: 300px;

            .payment-method {
                .card-icon {
                    margin-bottom: 20px;
                }
                .card-info {
                    margin-bottom: 20px;
                }
                .card-status {
                    p {
                        text-align: left;
                    }
                }
                .card-delete {
                    margin-right: 30px;
                }
            }
        }
    }
</style>
